import { Link, useParams } from "react-router-dom";
import {
  MessageCircle,
  CheckCircle2,
  AlertCircle,
  Send,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";
import { Seo } from "../components/Seo";
import { getCategory, categories, waLink, waCategoryMessage } from "../data/store";
import NotFound from "./NotFound";

export default function Kategori() {
  const { slug } = useParams();
  const category = getCategory(slug);

  if (!category) return <NotFound />;

  const others = categories.filter((c) => c.slug !== slug).slice(0, 4);
  const Icon = category.icon;

  return (
    <>
      <Seo
        title={`${category.name} | Toko Bangunan NJA Bajawa`}
        description={`${category.shortDesc} Tersedia di Toko Bangunan Nuansa Jaya Abadi, Bajawa. Tanya harga dan ketersediaan ${category.name} via WhatsApp.`}
        path={`/produk/${category.slug}`}
      />

      <section className="mx-auto max-w-[1200px] px-4 py-10 sm:px-6 lg:py-16">
        <nav aria-label="Breadcrumb" className="text-sm text-slate-500">
          <Link to="/produk" data-testid="breadcrumb-produk" className="inline-flex items-center gap-1 font-medium text-brand hover:underline">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Semua Kategori
          </Link>
        </nav>

        <div className="mt-6 flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div className="max-w-2xl">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-brand/10 text-brand">
              <Icon className="h-7 w-7" aria-hidden="true" />
            </span>
            <h1 className="mt-4 text-3xl font-extrabold text-navy sm:text-4xl">
              {category.name} di Bajawa
            </h1>
            <p className="mt-4 text-base leading-relaxed text-slate-600 md:text-lg">{category.intro}</p>
          </div>
          <a
            href={waLink(waCategoryMessage(category.name))}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="category-whatsapp-button"
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-wa px-6 py-3.5 text-base font-semibold text-white transition-colors duration-300 hover:bg-wa-dark"
          >
            <MessageCircle className="h-5 w-5" aria-hidden="true" />
            Tanya Harga {category.name}
          </a>
        </div>

        <div className="mt-10 grid gap-4 md:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-navy">
              <CheckCircle2 className="h-5 w-5 text-brand" aria-hidden="true" />
              Umum digunakan untuk
            </h2>
            <ul className="mt-4 space-y-2 text-sm leading-relaxed text-slate-600">
              {category.kegunaan.map((item) => (
                <li key={item} className="flex gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-navy">
              <AlertCircle className="h-5 w-5 text-brand" aria-hidden="true" />
              Hal yang perlu dipertimbangkan
            </h2>
            <ul className="mt-4 space-y-2 text-sm leading-relaxed text-slate-600">
              {category.pertimbangan.map((item) => (
                <li key={item} className="flex gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-navy">
              <Send className="h-5 w-5 text-brand" aria-hidden="true" />
              Siapkan info ini saat bertanya
            </h2>
            <ul className="mt-4 space-y-2 text-sm leading-relaxed text-slate-600">
              {category.infoDitanyakan.map((item) => (
                <li key={item} className="flex gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-10 rounded-2xl bg-navy p-6 text-white shadow-lg sm:p-8">
          <h2 className="text-lg font-semibold sm:text-xl">
            Cek harga dan ketersediaan {category.name}
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-white/80">
            Klik tombol di bawah — pesan WhatsApp sudah terisi otomatis, Anda
            tinggal melengkapi kebutuhan Anda.
          </p>
          <a
            href={waLink(waCategoryMessage(category.name))}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="category-whatsapp-cta"
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-wa px-6 py-3 text-sm font-semibold text-white transition-colors duration-300 hover:bg-wa-dark"
          >
            <MessageCircle className="h-5 w-5" aria-hidden="true" />
            Tanya via WhatsApp
          </a>
        </div>

        <nav aria-label="Kategori lainnya" className="mt-10">
          <h2 className="text-lg font-semibold text-navy">Kategori lainnya</h2>
          <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {others.map((c) => (
              <li key={c.slug}>
                <Link
                  to={`/produk/${c.slug}`}
                  data-testid={`related-category-${c.slug}`}
                  className="group flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-4 text-sm font-medium text-navy shadow-sm transition-shadow duration-300 hover:shadow-md"
                >
                  {c.name}
                  <ArrowRight className="h-4 w-4 text-brand transition-transform duration-300 group-hover:translate-x-0.5" aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </section>
    </>
  );
}
